import React from 'react';
import styled, { css } from 'styled-components';
import {
  brandColours,
  fontSize,
  maxBreakpointQuery,
  minBreakpointQuery,
  standardTransition,
  visuallyHidden,
} from '../styles';
import { Link } from './ui';
import rocket from '../images/rocket.svg';

const StyledDefaultNav = styled.div`
  ${({ hasAdditionalContent }) => {
    if (hasAdditionalContent) {
      return css`
        ${minBreakpointQuery.mlarge`
          position:relative;
          display: grid;
          gap: 90px;
          grid-template-columns: .7fr 1fr;
        `}

        ${minBreakpointQuery.xlarge`
          grid-template-columns: .6fr 1fr;
        `}

        ${minBreakpointQuery.xxxlarge`
          grid-template-columns: .5fr 1fr;
        `}
      `;
    }
  }}
`;

const StyledOverline = styled.p`
  ${maxBreakpointQuery.mlarge`
    ${visuallyHidden()};
  `}
`;

const StyledText = styled.p`
  ${maxBreakpointQuery.mlarge`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.mlarge`
    ${fontSize(21)};
    line-height: 1.4;
    margin-top: 15px;
  `}

  ${minBreakpointQuery.large`
    ${fontSize(23)};
  `}

  ${minBreakpointQuery.xlarge`
    ${fontSize(25)};
  `}
`;

const StyledIllustration = styled.img`
  ${maxBreakpointQuery.mlarge`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.mlarge`
    position: absolute;
    bottom: -50px;
    left: -30px;
    height: 204px;
    width: 220px;

    ${minBreakpointQuery.large`
      left: -60px;
      height: 242px;
      width: 260px;
    `}

    ${minBreakpointQuery.xlarge`
      left: -90px;
      bottom: -60px;
      height: 278px;
      width: 300px;
    `}

    ${minBreakpointQuery.xxxlarge`
      left: -120px;
      bottom: -70px;
      height: 316px;
      width: 340px;
    `}
  `}
`;

const StyledList = styled.ul`
  display: grid;
  gap: 10px;

  ${minBreakpointQuery.mlarge`
    grid-template-columns: ${({ hasAdditionalContent }) =>
      hasAdditionalContent ? 'repeat(3, 1fr);' : 'repeat(4, 1fr)'};
    column-gap: 15px;
  `}

  ${minBreakpointQuery.xlarge`
    column-gap: 20px;
    row-gap: 15px;
  `}

  ${minBreakpointQuery.xxxlarge`
    row-gap: 20px;
  `}
`;

const StyledListItem = styled.li`
  ${fontSize(14)};
  height: 100%;

  ${minBreakpointQuery.xlarge`
    ${fontSize(15)};
  `}

  ${minBreakpointQuery.xxxlarge`
    ${fontSize(16)};
  `}
`;

const StyledLink = styled(Link)`
  text-align: center;
  line-height: 1.5;
  transition: ${standardTransition('color')};
  height: 100%;

  ${minBreakpointQuery.mlarge`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
  `}

  ${minBreakpointQuery.xlarge`
    gap: 8px;
  `}

  ${minBreakpointQuery.xxxlarge`
    gap: 10px;
  `}

  &:hover {
    color: ${brandColours.tertiary};
  }
`;

const StyledImage = styled.img`
  ${maxBreakpointQuery.mlarge`
    ${visuallyHidden()};
  `};

  ${minBreakpointQuery.mlarge`
    max-width: 300px;
    max-height: 215px;
    width: 100%;
    height: 100%;
    background-color: ${brandColours.primary};
    padding: 30px 45px;
    border-radius: 15px;
    aspect-ratio: 6/4;
  `};

  ${minBreakpointQuery.xxxlarge`
    aspect-ratio: 5/4;
  `}
`;

const DefaultNav = ({ overline, text, items }) => {
  const hasAdditionalContent = overline || text ? true : false;

  return (
    <StyledDefaultNav hasAdditionalContent={hasAdditionalContent}>
      {hasAdditionalContent && (
        <div>
          {overline && <StyledOverline>{overline}</StyledOverline>}
          {text && <StyledText>{text}</StyledText>}
          <StyledIllustration src={rocket} alt="Rocket" loading="lazy" />
        </div>
      )}
      <StyledList hasAdditionalContent={hasAdditionalContent}>
        {items.map(({ title, slug, link, image }, id) => (
          <StyledListItem key={id}>
            <StyledLink to={link ? link.pageUrl : slug}>
              <StyledImage src={image.url} alt={image.alt} loading="lazy" />
              {link ? link.text : title}
            </StyledLink>
          </StyledListItem>
        ))}
      </StyledList>
    </StyledDefaultNav>
  );
};

export default DefaultNav;
